import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu'
import invariant from 'tiny-invariant'
import * as styles from './quick-view-selector.css'
import { StyledExpander, StyledItemIndicator } from './styled-dropdown-menu'

export type QuickViewSelectItem<TId extends string> = {
  label: string
  value: TId
  count?: number
}

export type QuickViewSelectorProps<TId extends string> = {
  quickViews: readonly QuickViewSelectItem<TId>[]
  selectedQuickView: TId
  onSelectedQuickViewChange: (view: TId) => unknown
  currentCount?: number
}

export const QuickViewSelector = <TId extends string>({
  quickViews,
  selectedQuickView,
  onSelectedQuickViewChange,
  currentCount,
}: QuickViewSelectorProps<TId>) => {
  const selectedView = quickViews.find(view => view.value === selectedQuickView)
  invariant(selectedView, 'the selected quick view must appear in the list of quick views')

  const handleValueChange = (value: string) => {
    onSelectedQuickViewChange(value as TId)
  }

  const displayCount = currentCount ?? selectedView.count

  /** One definition for both forms; only the interactive one earns the caret. */
  const labelFor = (isInteractive: boolean) => (
    <>
      <span className={styles.viewingLabel} aria-hidden="true">
        Viewing:
      </span>
      <div className={styles.triggerContent}>
        <span className={styles.triggerLabel}>{selectedView.label}</span>
        {displayCount !== undefined && <span className={styles.triggerCount}>({displayCount})</span>}
        {isInteractive && <StyledExpander />}
      </div>
    </>
  )

  // One view is not a choice. A caret and a menu offering a single item promise alternatives that do
  // not exist, and open onto nothing; the label and the count are the parts still worth showing, which
  // is why the selector is not withdrawn outright when the other views are unavailable.
  if (quickViews.length <= 1) {
    return <div className={styles.staticTrigger}>{labelFor(false)}</div>
  }

  return (
    <RadixDropdownMenu.Root>
      <RadixDropdownMenu.Trigger
        className={styles.trigger}
        aria-label={getQuickViewAccessibleName({ label: selectedView.label, count: displayCount })}
      >
        {labelFor(true)}
      </RadixDropdownMenu.Trigger>

      <RadixDropdownMenu.Portal>
        <RadixDropdownMenu.Content onCloseAutoFocus={e => e.preventDefault()} className={styles.content}>
          <RadixDropdownMenu.RadioGroup
            value={selectedQuickView}
            onValueChange={handleValueChange}
            className={styles.radioGroup}
          >
            {quickViews.map(view => (
              <QuickViewSelectorRadioItem key={view.value} view={view} />
            ))}
          </RadixDropdownMenu.RadioGroup>
        </RadixDropdownMenu.Content>
      </RadixDropdownMenu.Portal>
    </RadixDropdownMenu.Root>
  )
}

/**
 * The label and the count render as separate elements with no whitespace between them, so the name
 * assistive technology announces would otherwise depend on the computed `display` of those elements.
 * Stating it outright keeps the announced name the same in a browser and under jsdom, whose answer
 * changed in version 30 because it sees none of the stylesheet.
 */
const getQuickViewAccessibleName = ({ label, count }: { label: string; count?: number }) =>
  count === undefined ? label : `${label} (${count})`

const QuickViewSelectorRadioItem = <TId extends string>({ view }: { view: QuickViewSelectItem<TId> }) => {
  return (
    <RadixDropdownMenu.RadioItem
      className={styles.radioItem}
      value={view.value}
      disabled={view.count === 0}
      aria-label={getQuickViewAccessibleName(view)}
    >
      <span className={styles.radioItemLabel}>{view.label}</span>
      {view.count !== undefined && <span className={styles.radioItemCount}>({view.count})</span>}
      <StyledItemIndicator />
    </RadixDropdownMenu.RadioItem>
  )
}
